import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { ClientPageTitle } from "./client-page-components/client-page-title.component";
import { ClientMenu } from "./client-page-components/client-menu/client-menu.component";
import { ClientJobList } from "./client-page-components/client-job-list/client-job-list.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { JobListPortal } from "../../components/job-list/job-list-portal.component";
import React, { useEffect, useState } from "react";
import { ClientNotFoundComponent } from "./client-components/client-not-found.component";
import { Loading } from "@/components/src/animations/loading";
import { useCentralStore } from "../../store/Central";
import { useJobStore } from "../../store/Jobs";
import { Logger } from "@/lib/logger/Logger";
import { PlusCircle, Briefcase, Loader2, ClipboardList } from "lucide-react";
import { JobStatusEnum } from "@/lib/types/job";
import { cn } from "../../lib/utils/cn";

export const ClientPage: React.FC = () => {
	const { clientId } = useParams();
	const createJob = useJobStore((state) => state.createJob);
	const navigate = useNavigate();
	const location = useLocation();
	const [isJobListView, setIsJobListView] = useState(false);
	const [isCreatingJob, setIsCreatingJob] = useState(false);

	// Determine if we're on a job-related route
	const isOnJobRoute = () => {
		const path = location.pathname;
		const baseClientPath = `/client/${clientId}`;

		// Job list should be open for:
		// - Client dashboard (exact match)
		// - Job page (/client/:clientId/job/:jobId)
		return (
			path === baseClientPath || path.includes(`${baseClientPath}/job/`)
		);
	};

	const shouldShowJobList = isOnJobRoute() && isJobListView;

	const handleCreateJob = async (status?: JobStatusEnum) => {
		if (isCreatingJob) return;
		setIsCreatingJob(true);
		try {
			const job = await createJob(undefined, status);
			Logger.log("handleCreateJob", { job });
			if (job) {
				navigate(`/client/${job.client_id}/job/${job.id}`);
			}
		} finally {
			setIsCreatingJob(false);
		}
	};

	const { client, clientLoading, clientsLoading } = useCentralStore(
		(state) => ({
			client: state.client,
			clientLoading: state.clientLoading,
			clientsLoading: state.clientsLoading,
		})
	);

	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === "j" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				handleCreateJob();
			}
		};
		document.addEventListener("keydown", down);
		return () => document.removeEventListener("keydown", down);
	}, []);

	// Open job list view when navigating to job routes
	useEffect(() => {
		if (isOnJobRoute()) {
			setIsJobListView(true);
		}
	}, [location.pathname]);

	if (clientsLoading || clientLoading) return <Loading />;
	if (!client) return <ClientNotFoundComponent />;

	const handleJobsTabClick = () => {
		// Navigate to client dashboard when clicking Aufträge
		if (!isOnJobRoute()) {
			navigate(`/client/${clientId}`);
		}
	};
	return (
		<>
			{/* Portal to render client title and menu in header */}
			<HeaderPortal>
				<div className="flex items-center justify-between w-full gap-1">
					<ClientPageTitle />
					<div className="flex items-center gap-1">
						{/* Jobs tab - only highlighted when on job routes */}
						<button
							onClick={handleJobsTabClick}
							className={cn(
								"flex items-center gap-1 px-3 py-1 text-sm rounded-md transition-colors",
								isOnJobRoute()
									? "bg-stone-200 text-gray-800 font-medium"
									: "text-gray-700 hover:bg-stone-100"
							)}
							aria-expanded={shouldShowJobList}
							aria-controls="job-list-content"
						>
							<Briefcase size={16} className="mr-1" />
							<span>Aufträge</span>
						</button>
						<ClientMenu />
					</div>
				</div>
			</HeaderPortal>

			{/* Job list portal with slide animation - only shown on job routes */}
			<JobListPortal
				isOpen={shouldShowJobList}
				onToggle={setIsJobListView}
			>
				<div className="h-full py-1 pl-1" id="job-list-content">
					<div className="relative bg-white rounded-xl shadow-sm overflow-hidden h-full">
						<div className="p-3">
							<div className="flex flex-col gap-1.5">
								<button
									onClick={() => handleCreateJob()}
									disabled={isCreatingJob}
									className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white rounded-md shadow-md transition-colors text-sm font-medium"
									aria-label="Neuer Auftrag"
									data-testid="create-job-button"
								>
									{isCreatingJob ? (
										<Loader2
											size={16}
											className="animate-spin"
										/>
									) : (
										<PlusCircle size={16} />
									)}
									<span>Neuer Auftrag</span>
								</button>
								{/* <button
									onClick={() =>
										handleCreateJob(
											JobStatusEnum.PRE_REGISTERED
										)
									}
									disabled={isCreatingJob}
									className="flex items-center justify-center gap-2 w-full px-3 py-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-70 text-white rounded-md shadow-sm transition-colors text-xs font-medium"
									aria-label="Neue Voranmeldung"
									data-testid="create-pre-registered-job-button"
								>
									<ClipboardList size={14} />
									<span>Neue Voranmeldung</span>
								</button> */}
							</div>
						</div>
						<ClientJobList />
					</div>
				</div>
			</JobListPortal>

			<Outlet context={{ isJobListView, setIsJobListView }} />
		</>
	);
};
